import { useQuery } from '@apollo/client';
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import cx from 'classnames';
import withStyles from "isomorphic-style-loader/withStyles";
import { flowRight as compose } from 'lodash';
import { injectIntl } from "react-intl";

// Components
import Feedback from '../../components/Feedback';
import HomeSlider from "../../components/Home/HomeSlider";
import PopularLocationGrid from "../../components/Home/PopularLocationGrid";
import SeeAll from "../../components/Home/SeeAll";
import Loader from "../../components/Loader";
import { withQuery } from "../../hoc/withGraphql";

// GraphQL
import {
  getImageBannerData, GetMostViewedListing, getPopularLocationAdmin,
  getPopularNearby,
  getRecommend, getStaticInfoData,
  getStayInCity,
  getUserLocationName
} from "../../lib/graphql";

// Skeleton Loader
import PersonajeRegistrandoseImg from "../../../public/SiteImages/personaje-registrandose.svg";
import PersonajeVerificadoImg from "../../../public/SiteImages/personaje-verificado.svg";
import { HostCtaArrowIcon } from '../../components/Common/Assets/SvgComponent/commonSvgIcon';
import LayoutSections from "../../components/Home/LayoutSections";
import HomePopularSkeleton from "../../components/Skeleton/HomePopularSkeleton";
import HomeSliderSkeleton from "../../components/Skeleton/HomeSliderSkeleton";
import Shimmer from "../../components/Skeleton/Shimmer";
import l from '../../components/Skeleton/Skeleton.css';
import s from "./Home.css";

const Homepage = ({
  layoutType,
  wholeData,
  getRecommendData,
  getMostViewedListingData,
  getPopularLocationData,
  getImageBannerData,
  getStaticBlockInfoData
}) => {
  const [userLocation, setUserLocation] = useState(null);
  const [locationName, setLocationName] = useState(null);

  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => { }
      );
    }
  }, []);

  const { data: popularNearbyData, loading: popularNearbyLoading } = useQuery(
    getPopularNearby,
    {
      variables: { lat: userLocation?.lat, lng: userLocation?.lng },
      skip: !userLocation,
      ssr: false,
    }
  );

  const { data: locationNameData } = useQuery(
    getUserLocationName,
    {
      variables: { lat: userLocation?.lat, lng: userLocation?.lng },
      skip: !userLocation,
      ssr: false,
    }
  );

  useEffect(() => {
    if (locationNameData?.getUserLocationName) {
      setLocationName(locationNameData.getUserLocationName);
    }
  }, [locationNameData]);

  const { data: stayInCityData } = useQuery(
    getStayInCity,
    { ssr: false }
  );

  let title, content, getHomeBanner;

  const feedbackSpacing = getStaticBlockInfoData?.getStaticInfo?.length
    ? !getStaticBlockInfoData?.getStaticInfo?.some(item => item?.isEnable === false)
    : false;

  if (wholeData) {
    title = wholeData?.title;
    content = wholeData?.content;
    getHomeBanner = wholeData?.getHomeBanner;
  }

  if (!wholeData) return <Loader type="text" />;

  return (
    <div className={s.root}>
      <div>
        <LayoutSections
          layoutType={layoutType}
          title={title}
          content={content}
          homeBannerImages={getHomeBanner}
          cx={cx}
          s={s}
        />

        {/* Ubicaciones populares */}
        {getPopularLocationData && getPopularLocationData.loading ? (
          <div className={cx(s.popularGridWidth)}>
            <div >
              <div className={cx(l.popularSkeletonTwoTitle)}>
                <Shimmer />
              </div>
              <div className='fourCardSkeleton'>
                {[1, 2, 3, 4].map(n => <HomePopularSkeleton key={n} />)}
              </div>
            </div>
          </div>
        ) : (
          getPopularLocationData?.getPopularLocationAdmin?.length > 0 &&
          <div className={cx(s.popularGridWidth)}>
            <PopularLocationGrid data={getPopularLocationData.getPopularLocationAdmin} />
          </div>
        )}
      </div>

      {/*  Recomendados */}
      {getRecommendData && getRecommendData.loading ? (
        <div className={s.homePageContainer}>
          <div className={cx(s.container, s.skeletonContainerPadding)}>
            <div className={cx(l.popularSkeletonTitle)}>
              <Shimmer />
            </div>
            <div className='fourCardSkeleton'>
              {[1, 2, 3, 4].map(n => <HomeSliderSkeleton key={n} />)}
            </div>
          </div>
        </div>
      ) : (
        getRecommendData?.getRecommend?.length > 0 &&
        <div className={s.homePageContainer}>
          <div className={s.container}>
            <div className={s.sliderHeadingFlex}>
              <h3 className={s.sectionTitle}>Alojamientos <span className={s.highlight}>destacados</span></h3>
              <SeeAll />
            </div>
            <HomeSlider data={getRecommendData.getRecommend} isRecommand={true} hideHeading={true} skeletonClass='fourCardSkeleton' />
          </div>
        </div>
      )}


      <div className={cx(s.homePageContainer, (!feedbackSpacing) && s.feedbackSpacingContainer)}>
        <Feedback />
      </div>


      {getMostViewedListingData && getMostViewedListingData.loading ? (
        <div className={s.homePageContainer}>
          <div className={cx(s.container, s.skeletonContainerPadding)}>
            <div className={cx(l.popularSkeletonTitle)}>
              <Shimmer />
            </div>
            <div className='fourCardSkeleton'>
              {[1, 2, 3, 4, 5, 6].map(n => <HomeSliderSkeleton key={n} />)}
            </div>
          </div>
        </div>
      ) : (
        getMostViewedListingData?.GetMostViewedListing?.length > 0 &&
        <div className={s.homePageContainer}>
          <div className={s.container}>
            <div className={s.sliderHeadingFlex}>
              <h3 className={s.sectionTitle}>Los más buscados</h3>
              <SeeAll />
            </div>
            <HomeSlider data={getMostViewedListingData.GetMostViewedListing} isRecommand={false} hideHeading={true} skeletonClass='fourCardSkeleton' navBar="mostView" />
          </div>
        </div>
      )}

      {popularNearbyLoading || (!popularNearbyData && userLocation) ? (
        <div className={s.homePageContainer}>
          <div className={cx(s.container, s.skeletonContainerPadding)}>
            <div className={cx(l.popularSkeletonTitle)}>
              <Shimmer />
            </div>
            <div className='fourCardSkeleton'>
              {[1, 2, 3, 4, 5, 6].map(n => <HomeSliderSkeleton key={n} />)}
            </div>
          </div>
        </div>
      ) : (
        popularNearbyData?.getPopularNearby?.length > 0 &&
        <div className={s.homePageContainer}>
          <div className={s.container}>
            <div className={s.sliderHeadingFlex}>
              <h3 className={s.sectionTitle}>
                Populares cerca  <span className={s.highlight}>{locationName ? `de ${locationName}` : 'de ti'}</span>
                {/* <img src="/SiteImages/personaje-ubicacion.svg" className={s.sectionTitleIcon} /> */}
              </h3>
              <SeeAll />
            </div>
            <HomeSlider
              data={popularNearbyData.getPopularNearby}
              isRecommand={false}
              hideHeading={true}
              skeletonClass='fourCardSkeleton'
              navBar="popularNearby"
            />
          </div>
        </div>
      )}

      {stayInCityData?.getStayInCity?.listings?.length > 0 && (
        <div className={s.homePageContainer}>
          <div className={s.container}>
            <div className={s.sliderHeadingFlex}>
              <h3 className={s.sectionTitle}>
                Quédate en  <span className={s.highlight}>{` ${stayInCityData.getStayInCity.cityName}`}</span>
                {/* <img src="/SiteImages/personaje-quedate-maleta.svg" className={cx(s.sectionTitleIcon, s.sectionTitleQuedateIcon)} /> */}
              </h3>
              <SeeAll />
            </div>
            <HomeSlider
              data={stayInCityData.getStayInCity.listings}
              isRecommand={false}
              hideHeading={true}
              skeletonClass='fourCardSkeleton'
              navBar="stayInCity"
            />
          </div>
        </div>
      )}

      {/* Call to action anfitrion */}

      {/* Conviértete en anfitrión */}
      <div className={s.hostCtaSection}>
        <div className={s.hostCtaGrid}>
          <div className={s.hostCtaText}>
            <h3 className={s.hostCtaTitle}>¿Tienes un alojamiento?</h3>
            <p className={s.hostCtaSubtitle}>
              Publícalo en menos de 10 minutos
              y comienza a generar ingresos
            </p>
          </div>
          <div className={s.hostCtaImageWrap}>
            <img src={PersonajeRegistrandoseImg} alt="Publica tu alojamiento desde tu celular" className={s.hostCtaImage} />
          </div>
          <div className={s.hostCtaBtnWrap}>
            <a href="/whyhost" className={s.hostCtaBtn}>
              Conviértete en anfitrión
              <HostCtaArrowIcon className={s.hostCtaBtnIcon} />
            </a>
          </div>
          <div className={s.hostCtaImageWrap}>
            <img src={PersonajeVerificadoImg} alt="Huéspedes verificados" className={s.hostCtaImage} />
          </div>
        </div>
      </div>

      {/*  Banner data */}
      {/* {getImageBannerData && getImageBannerData.loading ? (
        <div className={s.homePageContainer}>
          <div className={s.container}>
            <NewsBoxSkeleton />
          </div>
        </div>
      ) : (
        <div className={s.homePageContainer}>
          <div className={s.container}>
            <NewsBox data={getImageBannerData?.getImageBanner} />
          </div>
        </div>
      )} */}



      {/* <HomeTrustBadges /> */}

      {/* {getStaticBlockInfoData && getStaticBlockInfoData.loading ? (
        <div className="hidden-xs">
          <div className={s.container}>
            <HomeKindofTripSkeleton />
          </div>
        </div>
      ) : (
        <div className="hidden-xs">
          <div className={s.container}>
            <HomeKindofTrip data={getStaticBlockInfoData.getStaticInfo} />
          </div>
        </div>
      )} */}
    </div>
  );
};

Homepage.propTypes = {
  getRecommendData: PropTypes.shape({ loading: PropTypes.bool, getRecommend: PropTypes.array }),
  getImageBannerData: PropTypes.shape({ loading: PropTypes.bool, getImageBanner: PropTypes.object }),
  getMostViewedListingData: PropTypes.shape({ loading: PropTypes.bool, GetMostViewedListing: PropTypes.array }),
  getPopularLocationData: PropTypes.shape({ loading: PropTypes.bool, GetMostViewedListing: PropTypes.array }),
  getStaticBlockInfoData: PropTypes.shape({ loading: PropTypes.bool })
};

Homepage.defaultProps = {
  getRecommendData: { loading: true },
  getPopularLocationData: { loading: true },
  getMostViewedListingData: { loading: true },
  getImageBannerData: { loading: true },
  getStaticBlockInfoData: { loading: true }
};

export default compose(
  injectIntl,
  withStyles(s, l),
  withQuery(getRecommend, { name: "getRecommendData", options: { ssr: false } }),
  withQuery(getPopularLocationAdmin, { name: "getPopularLocationData", options: { ssr: false } }),
  withQuery(GetMostViewedListing, { name: "getMostViewedListingData", options: { ssr: false } }),
  withQuery(getImageBannerData, { name: "getImageBannerData", options: { ssr: false } }),
  withQuery(getStaticInfoData, { name: "getStaticBlockInfoData", options: { ssr: false } })
)(Homepage);